import React, { useState } from 'react';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Loader from '../Loader';
import PresetPicker from '../PresetPicker';

import { usePresetTemplatesQuery } from 'src/api/queries/templateQueries';
import { useAddPresetsMutation } from 'src/api/mutations/templateMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';

import styles from './PresetTemplatesModal.module.scss';

type PresetTemplatesModalProps = BaseModalProps & {
  onSuccess?: () => void;
};

export const PresetTemplatesModal: React.FC<PresetTemplatesModalProps> = ({
  onSuccess,
  ...modalProps
}) => {
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const printExportAvailable = usePrintExportAvailable();

  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(new Set());

  // Fetch presets
  const { data: presetsResult, isLoading: isLoadingPresets } = usePresetTemplatesQuery();
  const presets = presetsResult?.data?.presets ?? [];

  // Toggle preset selection
  const togglePreset = (presetId: string) => {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  };

  // Select all presets
  const selectAll = () => {
    setSelectedPresets(new Set(presets.map((p) => p.preset_id)));
  };

  // Clear all selections
  const clearAll = () => {
    setSelectedPresets(new Set());
  };

  // Add presets mutation
  const addPresetsMutation = useAddPresetsMutation({
    onSuccess: (response) => {
      const { created, errors } = response.data;

      if (errors && errors.length > 0) {
        showAlert(`Added ${created} templates. Some failed: ${errors.join(', ')}`);
      } else {
        showSuccess(`Successfully added ${created} template${created !== 1 ? 's' : ''}`);
      }

      closeModal();
      onSuccess?.();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const handleSubmit = () => {
    if (selectedPresets.size === 0) return;
    addPresetsMutation.mutate(Array.from(selectedPresets));
  };

  const isLoading = addPresetsMutation.isLoading;
  const allSelected = presets.length > 0 && selectedPresets.size === presets.length;

  return (
    <BaseModal {...modalProps}>
      <h3 className={styles.modalTitle}>Add Preset Templates</h3>
      <p className={styles.modalDescription}>
        Select pre-configured templates to add to your account. You can customize them later.
      </p>

      {isLoadingPresets ? (
        <div className={styles.loadingWrapper}>
          <Loader />
        </div>
      ) : presets.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No preset templates available.</p>
        </div>
      ) : (
        <>
          <div className={styles.selectAllRow}>
            <button
              type="button"
              className={styles.selectAllButton}
              onClick={allSelected ? clearAll : selectAll}
            >
              {allSelected ? 'Deselect All' : `Select All (${presets.length})`}
            </button>
            <span className={styles.selectedCount}>
              {selectedPresets.size} selected
            </span>
          </div>

          <div className={styles.presetsContainer}>
            <PresetPicker
              presets={presets}
              selected={selectedPresets}
              onToggle={togglePreset}
              printExportAvailable={printExportAvailable}
            />
          </div>
        </>
      )}

      <div className={styles.actions}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          mode="primary"
          size="small"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={selectedPresets.size === 0 || isLoading}
        >
          Add {selectedPresets.size > 0 ? `${selectedPresets.size} ` : ''}Template
          {selectedPresets.size !== 1 ? 's' : ''}
        </Button>
      </div>
    </BaseModal>
  );
};

export default PresetTemplatesModal;

